import { Box, Dialog, Flex, Link, Portal, Text } from '@chakra-ui/react'
import { LuCheck } from 'react-icons/lu'

import IconClose from '@/components/icons/IconClose'
import { H4 } from '@/components/ui/Headings'

import type { IconType } from 'react-icons'

interface PluginData {
  name: string
  category: string
  description: string
  accentColor: string
  icon: IconType
  features: string[]
}

interface ProUpgradeModalProps {
  isOpen: boolean
  onClose: () => void
  plugin: PluginData | null
}

export default function ProUpgradeModal({
  isOpen,
  onClose,
  plugin
}: ProUpgradeModalProps) {
  if (!plugin) return null

  const Icon = plugin.icon

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={(details) => {
        if (!details.open) onClose()
      }}
      placement="center"
    >
      <Portal>
        <Dialog.Backdrop bg="blackAlpha.700" />
        <Dialog.Positioner>
          <Dialog.Content
            maxW="480px"
            w="full"
            mx="16px"
            bg="bg.primary"
            border="1px solid"
            borderColor="border.subtle"
            borderRadius="20px"
            p="0"
            position="relative"
          >
            {/* Close button */}
            <Dialog.CloseTrigger asChild>
              <Box
                position="absolute"
                top="20px"
                right="20px"
                cursor="pointer"
                zIndex={1}
                onClick={onClose}
              >
                <IconClose boxSize="18px" color="text.tertiary" />
              </Box>
            </Dialog.CloseTrigger>

            <Dialog.Body px="32px" pt="32px" pb="32px">
              {/* Plugin icon + name */}
              <Flex direction="column" align="center" mb="24px">
                <Flex
                  w="56px"
                  h="56px"
                  borderRadius="16px"
                  bg={`${plugin.accentColor}26`}
                  align="center"
                  justify="center"
                  mb="16px"
                >
                  <Icon size={28} color={plugin.accentColor} />
                </Flex>
                <Flex align="center" gap="10px" mb="8px">
                  <Dialog.Title asChild>
                    <H4 color="text.primary" fontWeight={500}>
                      {plugin.name}
                    </H4>
                  </Dialog.Title>
                  <Text
                    textStyle="bodySuperSmall"
                    fontWeight={600}
                    px="10px"
                    py="3px"
                    borderRadius="9999px"
                    bgGradient="to-r"
                    gradientFrom="#7c3aed"
                    gradientTo="#2563eb"
                    color="white"
                    letterSpacing="0.05em"
                    textTransform="uppercase"
                  >
                    PRO
                  </Text>
                </Flex>
                <Text
                  textStyle="body"
                  color="text.secondary"
                  textAlign="center"
                >
                  {plugin.description}
                </Text>
              </Flex>

              {/* Features */}
              <Flex direction="column" gap="12px" mb="24px">
                {plugin.features.map((feature) => (
                  <Flex key={feature} align="center" gap="10px">
                    <Flex
                      w="22px"
                      h="22px"
                      borderRadius="full"
                      bg="rgba(34, 197, 94, 0.12)"
                      align="center"
                      justify="center"
                      flexShrink={0}
                    >
                      <LuCheck size={13} color="#22c55e" />
                    </Flex>
                    <Text textStyle="bodySmall" color="text.secondary">
                      {feature}
                    </Text>
                  </Flex>
                ))}
              </Flex>

              {/* Divider */}
              <Box
                h="1px"
                bg="border.subtle"
                mb="24px"
              />

              {/* CTA */}
              <Link
                href="mailto:michael@spacerunners.com"
                w="100%"
                display="flex"
                alignItems="center"
                justifyContent="center"
                h="48px"
                textStyle="body"
                fontWeight={500}
                borderRadius="10px"
                bg="accent.500"
                color="white"
                transition="all 0.3s ease"
                _hover={{
                  bg: 'accent.400',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 0 30px rgba(0, 112, 243, 0.3)',
                  textDecoration: 'none'
                }}
              >
                Contact Us for Pro Access
              </Link>
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  )
}

export type { PluginData }
